import React, {useEffect, useState} from "react";
import axios from "axios";
import {Building2, Clock, Edit3, Hash, Mail, Map, MapPin, Phone, ShieldCheck, User as UserIcon, X} from "lucide-react";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast} from "../../utils/utility";
import * as Constants from "../../utils/Constants";
import CountrySelector from "../shared/CountrySelector";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Pill from "../../components/ui/Pill";
import Avatar from "../../components/ui/Avatar";
import Spinner from "../../components/ui/Spinner";
import {cn} from "../../utils/cn";

const ROLE_LABELS = {
  SUPER_ADMIN:     "Super Admin",
  CLIENT_ADMIN:    "Admin",
  CLIENT_MANAGER:  "Manager",
  CLIENT_EMPLOYEE: "Employee",
  MOBI_TRACK:      "Track Point",
};

const ROLE_TONE = {
  SUPER_ADMIN: "brand",
  CLIENT_ADMIN: "info",
  CLIENT_MANAGER: "warning",
  CLIENT_EMPLOYEE: "neutral",
  MOBI_TRACK: "success",
};

const formatLastLogin = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {dateStyle: "medium", timeStyle: "short"});
};

/* ── Internal: labelled input row with leading icon ── */
const ProfileField = ({label, name, value, onChange, disabled, icon: Icon, type = "text", placeholder}) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-ink-700">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />}
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(
          "block w-full h-9 pl-9 pr-3 text-base rounded-md",
          "bg-white border border-ink-200 shadow-inset",
          "text-ink-900 placeholder:text-ink-400",
          "transition-colors duration-fast",
          "hover:border-ink-300",
          "focus:border-brand-500 focus:outline-none focus:ring focus:ring-offset-0",
          "disabled:bg-ink-50 disabled:text-ink-700 disabled:cursor-not-allowed"
        )}
      />
    </div>
  </div>
);

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState({});

  const lastLoginAt = (() => {
    try {
      const stored = localStorage.getItem(Constants.LOGGED_IN_USER);
      return stored ? JSON.parse(stored).lastLoginAt : null;
    } catch { return null; }
  })();

  useEffect(() => {
    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/users/${getLoggedInUserId()}`;
    axios.get(`${Constants.BASE_URL}${endPoint}`, {
      headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`},
    })
      .then((response) => {
        setUserData(response.data.responseData);
        setFormState(response.data.responseData);
      })
      .catch((err) => console.error("Error fetching user data:", err));
  }, []);

  const handleChange = (e) => {
    const {name, value} = e.target;
    setFormState((prev) => ({...prev, [name]: value}));
  };

  const handleCountryChange = (selected) => {
    setFormState((prev) => ({
      ...prev,
      isoCountryCode: selected.isoCode,
      countryCode: selected.dialingCode,
    }));
  };

  const handleSubmit = async () => {
    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/users/${getLoggedInUserId()}`;
    try {
      const response = await axios.put(
        `${Constants.BASE_URL}${endPoint}`, formState,
        {headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`}}
      );
      setUserData(response.data.responseData);
      setFormState(response.data.responseData);
      setIsEditing(false);
      showToast("Profile updated successfully", {type: "success"});
    } catch (error) {
      const msg = error?.response?.data?.responseMessage || error?.response?.data || "Failed to update profile. Please try again.";
      showToast(typeof msg === "string" ? msg : "Failed to update profile.", {type: "error"});
    }
  };

  if (!userData) {
    return (
      <div className="py-16 flex items-center justify-center gap-3 text-ink-500">
        <Spinner size="md" /><span className="text-sm">Loading profile…</span>
      </div>
    );
  }

  const roleLabel = ROLE_LABELS[userData.userRole] || userData.userRole || "—";
  const displayName = userData.fullName || userData.username || "User";

  return (
    <div className="max-w-[1100px] mx-auto space-y-5 pb-8">
      {/* Identity card */}
      <Card>
        <div className="p-6 flex items-center gap-4">
          <Avatar name={displayName} size="xl" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-ink-900 truncate">{displayName}</h2>
            <p className="text-sm text-ink-500 truncate">@{userData.username} · ID {userData.userId}</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Pill tone={ROLE_TONE[userData.userRole] || "neutral"} size="md">
                <ShieldCheck className="h-3 w-3" /> {roleLabel}
              </Pill>
              {lastLoginAt && (
                <span className="inline-flex items-center gap-1.5 text-xs text-ink-500">
                  <Clock className="h-3.5 w-3.5" />
                  Previous session: <span className="font-medium text-ink-700">{formatLastLogin(lastLoginAt)}</span>
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0 hidden sm:block">
            {isEditing ? (
              <Button variant="secondary" icon={X} onClick={() => { setIsEditing(false); setFormState(userData); }}>
                Cancel
              </Button>
            ) : (
              <Button variant="primary" icon={Edit3} onClick={() => setIsEditing(true)}>
                Edit profile
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Personal details */}
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h3 className="text-base font-semibold text-ink-900">Personal details</h3>
          <p className="text-sm text-ink-500 mt-1">Name, contact and role information.</p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <ProfileField label="Full name"   name="fullName"    value={formState.fullName}    onChange={handleChange} disabled={!isEditing} icon={UserIcon} />
          <ProfileField label="Email"       name="email"       value={formState.email}       onChange={handleChange} disabled={!isEditing} icon={Mail} />
          <ProfileField label="Phone number" name="phoneNumber" value={formState.phoneNumber} onChange={handleChange} disabled={!isEditing} icon={Phone} />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">Role</label>
            <div className="relative">
              <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              <div className="block w-full h-9 pl-9 pr-3 text-base rounded-md bg-ink-50 border border-ink-200 text-ink-700 flex items-center">
                {roleLabel}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Address */}
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h3 className="text-base font-semibold text-ink-900">Address</h3>
          <p className="text-sm text-ink-500 mt-1">Where you're based — used for compliance and reports.</p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <ProfileField label="Street address" name="streetAddress" value={formState.streetAddress} onChange={handleChange} disabled={!isEditing} icon={MapPin} />
          </div>
          <ProfileField label="City"        name="city"       value={formState.city}       onChange={handleChange} disabled={!isEditing} icon={Building2} />
          <ProfileField label="State"       name="state"      value={formState.state}      onChange={handleChange} disabled={!isEditing} icon={Map} />
          <ProfileField label="Postal code" name="postalCode" value={formState.postalCode} onChange={handleChange} disabled={!isEditing} icon={Hash} />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">Country</label>
            <CountrySelector
              value={formState.isoCountryCode}
              onChange={handleCountryChange}
              disabled={!isEditing}
            />
          </div>
        </div>
        {isEditing && (
          <div className="px-6 py-4 border-t border-ink-100 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setIsEditing(false); setFormState(userData); }}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
          </div>
        )}
      </Card>

      {/* Mobile-only edit button at the bottom */}
      <div className="sm:hidden">
        {isEditing ? (
          <Button variant="secondary" icon={X} className="w-full" onClick={() => { setIsEditing(false); setFormState(userData); }}>
            Cancel
          </Button>
        ) : (
          <Button variant="primary" icon={Edit3} className="w-full" onClick={() => setIsEditing(true)}>
            Edit profile
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
